import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import { AppContext } from '@inway/layout';
import React, { JSX, useContext } from 'react';
import arrowRotateRight from '../icons/arrowRotateRight';
import ban from '../icons/ban';
import circleNotch from '../icons/circleNotch';
import circleUser from '../icons/circleUser';
import cloud from '../icons/cloud';
import cloudSlash from '../icons/cloudSlash';
import emptySet from '../icons/emptySet';
import exclamationTriangle from '../icons/exclamationTriangle';
import hourglass from '../icons/hourglass';
import hourglassHalf from '../icons/hourglassHalf';
import moon from '../icons/moon';
import moonOverSun from '../icons/moonOverSun';
import plug from '../icons/plug';
import search from '../icons/search';
import signOut from '../icons/signOut';
import signOutAlt from '../icons/signOutAlt';
import spinner from '../icons/spinner';
import { IconFamily, IconStyles } from '../icons/styledIcon';
import sunBright from '../icons/sunBright';

const icons: {
  [key in LayoutIcons]: (
    family: IconFamily,
    style: IconStyles
  ) => IconDefinition;
} = {
  arrowRotateRight,
  ban,
  circleNotch,
  circleUser,
  cloud,
  cloudSlash,
  emptySet,
  exclamationTriangle,
  hourglass,
  hourglassHalf,
  moon,
  moonOverSun,
  plug,
  search,
  signOut,
  signOutAlt,
  spinner,
  sunBright,
};

export type LayoutIcons =
  | 'arrowRotateRight'
  | 'ban'
  | 'circleNotch'
  | 'circleUser'
  | 'cloud'
  | 'cloudSlash'
  | 'emptySet'
  | 'exclamationTriangle'
  | 'hourglass'
  | 'hourglassHalf'
  | 'moon'
  | 'moonOverSun'
  | 'plug'
  | 'search'
  | 'signOut'
  | 'signOutAlt'
  | 'spinner'
  | 'sunBright';

export interface LayoutIconProps extends Omit<FontAwesomeIconProps, 'icon'> {
  icon: LayoutIcons;
  iconFamily?: IconFamily;
  iconStyle?: IconStyles;
}

const LayoutIcon = React.forwardRef<SVGSVGElement, LayoutIconProps>(
  ({ icon, iconFamily = 'classic', iconStyle, ...props }, ref): JSX.Element => {
    const { theme } = useContext(AppContext);

    let family = iconFamily;
    let style = iconStyle;
    if (style == undefined) {
      if (theme.iconStyle === 'duotone') {
        family = 'duotone';
        style = 'thin';
      } else {
        family = 'classic';
        style = theme.iconStyle;
      }
    }

    if (icon in icons)
      return <FontAwesomeIcon icon={icons[icon](family, style)} {...props} />;

    console.error(
      `LayoutIcon: unable to recognize icon '${icon ?? '(undefined)'}'`
    );

    return (
      <FontAwesomeIcon
        ref={ref}
        icon={icons['emptySet'](family, style)}
        {...props}
        className="text-danger"
        pulse
      />
    );
  }
);
LayoutIcon.displayName = 'LayoutIcon';

export default LayoutIcon;
